/**
 * Seasonal-system catalog.
 *
 * Identifies which `system.kind` values have a clear "open / close"
 * cadence and provides hemisphere-aware defaults so the user can
 * accept the suggested months in one tap.
 *
 * The `active_start_month` and `active_end_month` columns on the
 * `system` table (schema v4) gate task generation — the planner skips
 * rules for systems whose active window is closed. Filling these
 * columns is what flips a pool from "we have one" to "open it in May,
 * close it in October."
 *
 * Month numbers are 1-12 (January = 1, December = 12). Northern
 * defaults assume a USA-ish climate (Apr-Oct outdoor season). Southern
 * is just shifted by 6 months — the hemisphere flag on property lets
 * us choose the right preset without separate per-country rules.
 */

export type Hemisphere = 'northern' | 'southern';

export interface SeasonalKindMeta {
  kind: string;
  /** Hemisphere-keyed defaults: open month / close month. */
  defaults: Record<Hemisphere, { open: number; close: number }>;
  /** Plain-English description shown beneath the picker so the user
   *  understands what "open" / "close" means for THIS kind of system
   *  (winterize the pool vs. blow out the sprinklers vs. shut off the
   *  hose bibs, etc.). */
  hint: string;
}

/**
 * The set we explicitly ask about during the seasonal walkthrough.
 * Keep this conservative — only kinds where the open/close window is
 * structurally important (i.e. the planner would scramble or skip
 * relevant tasks if it didn't know). Anything else can be tuned by
 * the user later from the per-system Edit screen.
 */
export const SEASONAL_KINDS: Record<string, SeasonalKindMeta> = {
  pool: {
    kind: 'pool',
    defaults: {
      northern: { open: 5, close: 10 },
      southern: { open: 11, close: 4 },
    },
    hint: 'When do you open the pool in spring and close it in the fall?',
  },
  pool_cover: {
    kind: 'pool_cover',
    defaults: {
      northern: { open: 5, close: 10 },
      southern: { open: 11, close: 4 },
    },
    hint: 'Match this to your pool — covers come off when the pool opens.',
  },
  irrig: {
    kind: 'irrig',
    defaults: {
      northern: { open: 4, close: 10 },
      southern: { open: 10, close: 4 },
    },
    hint: 'Sprinklers run during the growing season and get winterized after.',
  },
  sprinkler_controller: {
    kind: 'sprinkler_controller',
    defaults: {
      northern: { open: 4, close: 10 },
      southern: { open: 10, close: 4 },
    },
    hint: 'When the controller fires zones vs. sits dormant.',
  },
  hose_bib: {
    kind: 'hose_bib',
    defaults: {
      northern: { open: 4, close: 11 },
      southern: { open: 10, close: 5 },
    },
    hint: 'Outdoor spigots — shut off + drain before the first freeze.',
  },
  central_ac: {
    kind: 'central_ac',
    defaults: {
      northern: { open: 5, close: 9 },
      southern: { open: 11, close: 3 },
    },
    hint: 'Cooling season window — when AC is actively running.',
  },
  window_ac: {
    kind: 'window_ac',
    defaults: {
      northern: { open: 5, close: 9 },
      southern: { open: 11, close: 3 },
    },
    hint: 'Install for summer, store for winter.',
  },
  garden: {
    kind: 'garden',
    defaults: {
      northern: { open: 4, close: 10 },
      southern: { open: 10, close: 4 },
    },
    hint: 'The growing season — planting through put-to-bed.',
  },
  gas_grill: {
    kind: 'gas_grill',
    defaults: {
      northern: { open: 4, close: 10 },
      southern: { open: 10, close: 4 },
    },
    hint: 'Outdoor cooking window. Most users grill year-round in mild climates — skip if so.',
  },
};

export function isSeasonalKind(kind: string): boolean {
  return Object.prototype.hasOwnProperty.call(SEASONAL_KINDS, kind);
}

/**
 * Month name helper — full names ("January") for the picker labels.
 * Locale-aware so non-English users see month names in their language.
 */
export function monthName(month: number): string {
  const d = new Date(2024, Math.max(0, Math.min(11, month - 1)), 1);
  return d.toLocaleDateString(undefined, { month: 'long' });
}

export function monthShort(month: number): string {
  const d = new Date(2024, Math.max(0, Math.min(11, month - 1)), 1);
  return d.toLocaleDateString(undefined, { month: 'short' });
}
